import * as React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CommentIcon from '@mui/icons-material/Comment';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { whiteNotchedOutline } from '../../../../../../../shared/whiteNotchedOutline';
import { getCommentCompanyName } from '../state/utils';

const Form = styled('form')({
    display: 'flex',
    flexDirection: 'column'
});

const Fieldset = styled('fieldset')({
    margin: 0,
    padding: '12px 16px 16px',
    border: '1px solid #c5cdcd',
    borderRadius: '2px',
    backgroundColor: '#eceff0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
});

const Legend = styled('legend')({
    width: 'auto',
    padding: '0 4px',
    fontSize: '12px',
    textTransform: 'uppercase',
    color: '#555',
    border: 'none',
    marginBottom: 0
});

const SaveButton = styled(Button)({
    alignSelf: 'flex-end'
});

type ImageCommentFormProps = {
    isSaving: boolean;
    onSave: (text: string) => Promise<unknown>;
};

export function ImageCommentForm({ isSaving, onSave }: ImageCommentFormProps) {
    let [text, setText] = React.useState('');
    let trimmed = text.trim();
    let writeCommentLabel = Translate(
        'image-processing/modal/comments/textarea-label/write-comment-to-site',
        { site: getCommentCompanyName(window.appConfig.site) }
    );

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (trimmed.length === 0) {
            return;
        }
        onSave(trimmed).then(() => setText(''));
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Fieldset>
                <Legend>{Translate('image-processing/modal/comments/legend-label/comment')}</Legend>
                <Typography
                    component="label"
                    htmlFor="image-comment-text"
                    sx={{ fontSize: '14px', fontWeight: 600 }}
                >
                    {writeCommentLabel}
                </Typography>
                <TextField
                    id="image-comment-text"
                    multiline
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    size="small"
                    value={text}
                    disabled={isSaving}
                    sx={whiteNotchedOutline}
                    onChange={(event) => setText(event.target.value)}
                />
                <SaveButton
                    type="submit"
                    variant="contained"
                    disabled={isSaving || trimmed.length === 0}
                    aria-busy={isSaving}
                    startIcon={isSaving ? <CircularProgress size={18} /> : <CommentIcon />}
                >
                    {Translate('image-processing/modal/footer/button-label/save-comment')}
                </SaveButton>
            </Fieldset>
        </Form>
    );
}
